import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { MAX_TILE_HEIGHT, toWorldCell } from "../../shared/room-layout";
import type {
  DoorDirection,
  RoomLayoutDoor,
  RoomLayoutTile,
  RoomLayoutWallEdge,
} from "../../shared/types";

export type LayoutEditorDrawMode =
  | "draw"
  | "stairs"
  | "erase"
  | "door"
  | "raise"
  | "lower"
  | "wall";

export interface LayoutEditorGridCell {
  row: number;
  col: number;
}

interface Props {
  width: number;
  height: number;
  tiles: RoomLayoutTile[][];
  walls?: RoomLayoutWallEdge[];
  door: RoomLayoutDoor;
  drawMode: LayoutEditorDrawMode;
  wallDirection: DoorDirection;
  onPaintCells: (cells: LayoutEditorGridCell[]) => void;
}

const CANVAS_PADDING = 18;
const MIN_TILE_SIZE = 20;
const MAX_TILE_SIZE = 34;
const TARGET_EDITOR_SIZE = 560;

function tileBaseColor(tile: RoomLayoutTile, isDoor: boolean): string {
  if (!tile.walkable) return "hsl(221deg 18% 14%)";
  const normalized = Math.max(0, Math.min(1, tile.height / MAX_TILE_HEIGHT));
  if (tile.surface === "stairs") {
    const lightness = 42 + normalized * 14;
    return `hsl(31deg 68% ${lightness}%)`;
  }
  const hue = 200 - normalized * 84;
  const sat = 56 + normalized * 8;
  const lightness = 33 + normalized * 20;
  if (isDoor) {
    return `hsl(26deg 92% ${Math.min(68, lightness + 12)}%)`;
  }
  return `hsl(${hue}deg ${sat}% ${lightness}%)`;
}

function formatTileHeight(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function edgeKey(edge: Pick<RoomLayoutWallEdge, "x" | "z" | "direction">): string {
  return `${edge.x},${edge.z},${edge.direction}`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function sameCell(
  a: LayoutEditorGridCell | null,
  b: LayoutEditorGridCell | null
): boolean {
  if (!a || !b) return false;
  return a.row === b.row && a.col === b.col;
}

function rasterizeLine(
  from: LayoutEditorGridCell,
  to: LayoutEditorGridCell
): LayoutEditorGridCell[] {
  const points: LayoutEditorGridCell[] = [];
  let x0 = from.col;
  let y0 = from.row;
  const x1 = to.col;
  const y1 = to.row;
  const dx = Math.abs(x1 - x0);
  const sx = x0 < x1 ? 1 : -1;
  const dy = -Math.abs(y1 - y0);
  const sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;

  while (true) {
    points.push({ row: y0, col: x0 });
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) {
      err += dy;
      x0 += sx;
    }
    if (e2 <= dx) {
      err += dx;
      y0 += sy;
    }
  }
  return points;
}

function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  const r = Math.max(0, Math.min(radius, Math.min(width, height) / 2));
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

export function LayoutEditorCanvas({
  width,
  height,
  tiles,
  walls,
  door,
  drawMode,
  wallDirection,
  onPaintCells,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [hoveredCell, setHoveredCell] = useState<LayoutEditorGridCell | null>(null);
  const isPaintingRef = useRef(false);
  const strokeVisitedRef = useRef<Set<string>>(new Set());
  const lastPaintedCellRef = useRef<LayoutEditorGridCell | null>(null);

  const tileSize = useMemo(() => {
    const size = Math.floor(TARGET_EDITOR_SIZE / Math.max(width, height));
    return clamp(size, MIN_TILE_SIZE, MAX_TILE_SIZE);
  }, [height, width]);

  const editorWidth = CANVAS_PADDING * 2 + width * tileSize;
  const editorHeight = CANVAS_PADDING * 2 + height * tileSize;

  const wallSet = useMemo(
    () => new Set((walls ?? []).map((edge) => edgeKey(edge))),
    [walls]
  );

  const toCellAtPoint = useCallback(
    (clientX: number, clientY: number): LayoutEditorGridCell | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      const localX = clientX - rect.left - CANVAS_PADDING;
      const localY = clientY - rect.top - CANVAS_PADDING;
      const col = Math.floor(localX / tileSize);
      const row = Math.floor(localY / tileSize);
      if (row < 0 || row >= height || col < 0 || col >= width) {
        return null;
      }
      return { row, col };
    },
    [height, tileSize, width]
  );

  const stopStroke = useCallback(() => {
    isPaintingRef.current = false;
    strokeVisitedRef.current.clear();
    lastPaintedCellRef.current = null;
  }, []);

  const emitStrokeCells = useCallback(
    (cells: LayoutEditorGridCell[]) => {
      if (cells.length === 0) return;
      const visited = strokeVisitedRef.current;
      const unique: LayoutEditorGridCell[] = [];
      for (const cell of cells) {
        if (
          cell.row < 0 ||
          cell.row >= height ||
          cell.col < 0 ||
          cell.col >= width
        ) {
          continue;
        }
        const key = `${cell.row},${cell.col}`;
        if (visited.has(key)) continue;
        visited.add(key);
        unique.push(cell);
      }
      if (unique.length > 0) {
        onPaintCells(unique);
      }
    },
    [height, onPaintCells, width]
  );

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLCanvasElement>) => {
      if (event.button !== 0) return;
      const cell = toCellAtPoint(event.clientX, event.clientY);
      if (!cell) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      isPaintingRef.current = true;
      strokeVisitedRef.current = new Set();
      lastPaintedCellRef.current = cell;
      emitStrokeCells([cell]);
    },
    [emitStrokeCells, toCellAtPoint]
  );

  const handlePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLCanvasElement>) => {
      const cell = toCellAtPoint(event.clientX, event.clientY);
      setHoveredCell(cell);
      if (!isPaintingRef.current || !cell) {
        return;
      }
      const previous = lastPaintedCellRef.current;
      if (!previous) {
        emitStrokeCells([cell]);
        lastPaintedCellRef.current = cell;
        return;
      }
      if (sameCell(previous, cell)) {
        return;
      }
      emitStrokeCells(rasterizeLine(previous, cell));
      lastPaintedCellRef.current = cell;
    },
    [emitStrokeCells, toCellAtPoint]
  );

  const handlePointerUp = useCallback(
    (event: ReactPointerEvent<HTMLCanvasElement>) => {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      stopStroke();
    },
    [stopStroke]
  );

  useEffect(() => {
    window.addEventListener("pointerup", stopStroke);
    return () => window.removeEventListener("pointerup", stopStroke);
  }, [stopStroke]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;

    const dpr = window.devicePixelRatio || 1;
    const pixelWidth = Math.round(editorWidth * dpr);
    const pixelHeight = Math.round(editorHeight * dpr);
    if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
      canvas.width = pixelWidth;
      canvas.height = pixelHeight;
    }
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    context.clearRect(0, 0, editorWidth, editorHeight);

    const bg = context.createLinearGradient(0, 0, 0, editorHeight);
    bg.addColorStop(0, "#1a1e29");
    bg.addColorStop(1, "#0f1219");
    context.fillStyle = bg;
    context.fillRect(0, 0, editorWidth, editorHeight);

    context.fillStyle = "rgba(7, 10, 15, 0.55)";
    drawRoundedRect(
      context,
      CANVAS_PADDING - 8,
      CANVAS_PADDING - 8,
      width * tileSize + 16,
      height * tileSize + 16,
      10
    );
    context.fill();

    const layoutShape = { width, height };

    for (let row = 0; row < height; row++) {
      for (let col = 0; col < width; col++) {
        const tile = tiles[row]?.[col] ?? { walkable: false, height: 0 };
        const [x, z] = toWorldCell(layoutShape, row, col);
        const isDoor = door.x === x && door.z === z;
        const northWall = wallSet.has(`${x},${z},north`);
        const westWall = wallSet.has(`${x},${z},west`);

        const px = CANVAS_PADDING + col * tileSize;
        const py = CANVAS_PADDING + row * tileSize;
        const inner = Math.max(1, Math.floor(tileSize * 0.07));
        const cellX = px + inner;
        const cellY = py + inner;
        const cellSize = tileSize - inner * 2;

        context.fillStyle = tileBaseColor(tile, isDoor);
        drawRoundedRect(context, cellX, cellY, cellSize, cellSize, 5);
        context.fill();

        if (tile.walkable && tile.surface === "stairs") {
          context.save();
          drawRoundedRect(context, cellX, cellY, cellSize, cellSize, 5);
          context.clip();
          context.strokeStyle = "rgba(255, 255, 255, 0.22)";
          context.lineWidth = 1.2;
          for (let offset = -cellSize; offset < cellSize * 2; offset += 6) {
            context.beginPath();
            context.moveTo(cellX + offset, cellY + cellSize);
            context.lineTo(cellX + offset + cellSize, cellY);
            context.stroke();
          }
          context.restore();
        }

        context.strokeStyle = tile.walkable
          ? "rgba(255,255,255,0.18)"
          : "rgba(255,255,255,0.1)";
        context.lineWidth = 1;
        drawRoundedRect(context, cellX, cellY, cellSize, cellSize, 5);
        context.stroke();

        if (northWall) {
          context.strokeStyle = "rgba(255, 242, 185, 0.98)";
          context.lineWidth = 3;
          context.beginPath();
          context.moveTo(cellX + 1, cellY + 1);
          context.lineTo(cellX + cellSize - 1, cellY + 1);
          context.stroke();
        }
        if (westWall) {
          context.strokeStyle = "rgba(255, 242, 185, 0.98)";
          context.lineWidth = 3;
          context.beginPath();
          context.moveTo(cellX + 1, cellY + 1);
          context.lineTo(cellX + 1, cellY + cellSize - 1);
          context.stroke();
        }

        if (tile.walkable) {
          context.font = `${Math.max(10, Math.floor(tileSize * 0.33))}px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace`;
          context.fillStyle = "rgba(255,255,255,0.95)";
          context.textAlign = "center";
          context.textBaseline = "middle";
          context.fillText(
            formatTileHeight(tile.height),
            px + tileSize / 2,
            py + tileSize / 2 + 1
          );
        }

        if (tile.walkable && tile.surface === "stairs") {
          context.font = `${Math.max(9, Math.floor(tileSize * 0.28))}px ui-sans-serif, system-ui, sans-serif`;
          context.textAlign = "left";
          context.textBaseline = "top";
          context.fillStyle = "rgba(255,255,255,0.9)";
          context.fillText("S", cellX + 3, cellY + 2);
        }

        if (isDoor) {
          context.strokeStyle = "rgba(255, 193, 122, 0.98)";
          context.lineWidth = 2.2;
          drawRoundedRect(context, cellX, cellY, cellSize, cellSize, 5);
          context.stroke();

          context.textAlign = "right";
          context.textBaseline = "top";
          context.fillStyle = "rgba(255,248,228,0.98)";
          context.font = `${Math.max(9, Math.floor(tileSize * 0.26))}px ui-sans-serif, system-ui, sans-serif`;
          context.fillText("D", cellX + cellSize - 3, cellY + 2);

          const centerX = px + tileSize / 2;
          const centerY = py + tileSize / 2;
          const arrowLen = Math.max(6, Math.floor(tileSize * 0.25));
          let toX = centerX;
          let toY = centerY;
          if (door.direction === "north") toY -= arrowLen;
          if (door.direction === "south") toY += arrowLen;
          if (door.direction === "west") toX -= arrowLen;
          if (door.direction === "east") toX += arrowLen;
          context.strokeStyle = "rgba(255,245,218,0.94)";
          context.lineWidth = 1.7;
          context.beginPath();
          context.moveTo(centerX, centerY);
          context.lineTo(toX, toY);
          context.stroke();
        }
      }
    }

    if (hoveredCell) {
      const px = CANVAS_PADDING + hoveredCell.col * tileSize;
      const py = CANVAS_PADDING + hoveredCell.row * tileSize;
      context.strokeStyle = "rgba(143, 205, 255, 0.95)";
      context.lineWidth = 2;
      drawRoundedRect(
        context,
        px + 1.5,
        py + 1.5,
        tileSize - 3,
        tileSize - 3,
        6
      );
      context.stroke();

      if (drawMode === "wall") {
        context.strokeStyle = "rgba(255, 242, 185, 0.98)";
        context.lineWidth = 3;
        if (wallDirection === "north") {
          context.beginPath();
          context.moveTo(px + 4, py + 4);
          context.lineTo(px + tileSize - 4, py + 4);
          context.stroke();
        } else if (wallDirection === "west") {
          context.beginPath();
          context.moveTo(px + 4, py + 4);
          context.lineTo(px + 4, py + tileSize - 4);
          context.stroke();
        }
      }
    }
  }, [
    door,
    drawMode,
    editorHeight,
    editorWidth,
    height,
    hoveredCell,
    tileSize,
    tiles,
    wallDirection,
    wallSet,
    width,
  ]);

  return (
    <div
      style={{
        border: "1px solid rgba(255,255,255,0.14)",
        borderRadius: 12,
        overflow: "auto",
        background:
          "radial-gradient(circle at 20% 0%, rgba(69,83,112,0.35), transparent 46%), linear-gradient(180deg, rgba(24,27,33,0.85), rgba(13,15,19,0.82))",
      }}
    >
      <canvas
        ref={canvasRef}
        width={editorWidth}
        height={editorHeight}
        style={{
          display: "block",
          width: editorWidth,
          height: editorHeight,
          touchAction: "none",
          cursor: drawMode === "wall" ? "crosshair" : "cell",
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={() => setHoveredCell(null)}
        onContextMenu={(event) => event.preventDefault()}
      />
    </div>
  );
}
